import i18n from "@/providers/i18n";
import type { Deployment, UserProfile, Workspace } from "@/supabase.types";

export const userName = (user?: UserProfile | null) => {
  if (!user) return "";
  if (!user.first_name && !user.last_name) return user.email;
  return `${user.first_name} ${user.last_name}`.trim();
};

export const deploymentName = (deployment?: Partial<Deployment> | null, workspace?: Partial<Workspace> | null) => {
  let ws_name = "";
  if (workspace?.name) {
    ws_name = `(${workspace.name})`;
  }
  let depl_name = "";
  if (deployment?.name) {
    depl_name = deployment.name;
    if (ws_name) {
      depl_name += ` ${ws_name}`;
    }
  }
  return depl_name;
};

export const addressName = ({
  address_1,
  address_2,
  city,
  zipcode,
  country,
}: Pick<Partial<Deployment | Workspace>, "address_1" | "address_2" | "city" | "zipcode" | "country">) => {
  return [address_1, address_2, city, zipcode, country].filter((v) => !!v).join(", ");
};

export function byKey<T extends Record<string, unknown>, K extends T[keyof T] & string>(
  collection: T[],
  key: keyof T,
): Partial<Record<K, T>> {
  return collection.reduce(
    (reduceObj, item) => {
      reduceObj[item[key] as K] = item;
      return reduceObj;
    },
    {} as Record<K, T>,
  );
}

export const groupBy = <T extends Record<string, unknown>>(
  arr: T[],
  key: string & keyof T,
  initial = {} as Record<string, T[]>,
): Record<string, T[]> => {
  if (arr.length <= 0) return initial;
  return arr.reduce((reduceObj, item) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (reduceObj[item[key] as string] = reduceObj[item[key] as string] || []).push(item);
    return reduceObj;
  }, initial);
};

export const partitionArrayByFilter = <T>(array: T[], filter: (item: T, index: number, array: T[]) => boolean) => {
  const pass: T[] = [],
    fail: T[] = [];
  array.forEach((item, index, array) => (filter(item, index, array) ? pass : fail).push(item));
  return [pass, fail];
};

// source: https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
export function filterNotEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  if (value === null || value === undefined) return false;
  return true;
}

export function filterEmptyObjectFields<T extends Record<string, unknown>>(object: T): T {
  const _object = Object.assign({}, object);
  Object.keys(_object).forEach((key) => {
    if (_object[key] === undefined) {
      delete _object[key];
    }
  });
  return _object;
}

export function isDefined<T>(value: T | undefined | null): value is T {
  return (value as T) !== undefined && (value as T) !== null;
}

export const formatNumberDecimals = (value?: number | null, decimals = 0) => {
  if (!isDefined(value)) return null;
  const factor = Math.pow(10, decimals);
  const preresult = value * factor * (1 + Number.EPSILON);
  return Math.round(preresult) / factor;
};

export function stringifyObjectValues(myObj: Record<string, unknown>): Record<string, string> {
  const stringifiedObj = Object.assign({}, myObj);
  Object.keys(myObj).forEach((key) => {
    if (typeof stringifiedObj[key] === "object") {
      stringifiedObj[key] = JSON.stringify(stringifiedObj[key]);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      stringifiedObj[key] = String(stringifiedObj[key]);
    }
  });
  return stringifiedObj as Record<string, string>;
}

interface SortOptions {
  order?: "asc" | "desc";
  locale?: string;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepValue = <T extends Record<string, any>, K extends keyof T>(keys: K[], obj?: T): T[K] | undefined => {
  const key = keys.shift();
  if (key === undefined || !obj || !(key in obj)) return undefined;
  const value = obj[key];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  if (keys.length > 0 && typeof value === "object") return deepValue(keys, value);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return value;
};

const basicSort = (a: string | number, b: string | number, locale: string): number => {
  const sortCollator = new Intl.Collator(locale, { usage: "sort", numeric: true });
  return sortCollator.compare(a.toString(), b.toString());
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const sortObjectsBy = <T extends Record<string, unknown>, K extends keyof T>(
  key: string,
  sortOptions: SortOptions = {},
) => {
  const defaultSortOptions = { order: "asc", locale: i18n.resolvedLanguage ?? "en" };
  const options = { ...defaultSortOptions, ...sortOptions };
  const keys = key.split(".") as K[];

  return (a?: T, b?: T): number => {
    const vA = deepValue([...keys], a);
    const vB = deepValue([...keys], b);
    if (
      vA === null ||
      vA === undefined ||
      vB === null ||
      vB === undefined ||
      (typeof vA !== "string" && typeof vA !== "number") ||
      (typeof vB !== "string" && typeof vB !== "number")
    ) {
      return 0;
    }
    return options.order === "asc" ? basicSort(vA, vB, options.locale) : basicSort(vB, vA, options.locale);
  };
};

export const filterUniqueObjectsArray = <T extends { id: string }>(items: T[]): T[] => {
  return items.filter((obj, index, self) => index === self.findIndex((o) => o.id === obj.id));
};

export const getSafePercentage = (a?: number, b?: number) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (a === 0 || !a) && (b === 0 || !b) ? 0 : Math.round((a! / b!) * 100);
};

export const moveArrayItem = <T>(items: T[], oldIndex: number, newIndex: number) => {
  const temp_items: (T | undefined)[] = [...items];
  if (newIndex >= temp_items.length) {
    let k = newIndex - temp_items.length + 1;
    while (k--) {
      temp_items.push(undefined);
    }
  }
  temp_items.splice(newIndex, 0, temp_items.splice(oldIndex, 1)[0]);
  return temp_items.filter(filterNotEmpty); // for testing
};

export const safeFormatAddress = <
  T extends {
    address_1?: string | null;
    address_2?: string | null;
    city?: string | null;
    country?: string | null;
    zipcode?: string | null;
  },
>(
  values: T,
): T & {
  address_1: string;
  address_2: string;
  city: string;
  country: string;
  zipcode: string;
} => {
  return {
    ...values,
    address_1: values.address_1 ? values.address_1 : "",
    address_2: values.address_2 ? values.address_2 : "",
    city: values.city ? values.city : "",
    country: values.country ? values.country : "",
    zipcode: values.zipcode ? values.zipcode : "",
  };
};

type FilterUndefined<T> = T extends undefined ? never : T;
type FilterNull<T> = T extends null ? never : T;
type FilterUndefinedAndNull<T> = FilterUndefined<FilterNull<T>>;

type ExtractFromObject<O extends Record<PropertyKey, unknown>, K> = K extends keyof O
  ? O[K]
  : K extends keyof FilterUndefinedAndNull<O>
    ? FilterUndefinedAndNull<O>[K] | undefined
    : undefined;

type ExtractFromArray<A extends readonly unknown[], K> = unknown[] extends A
  ? A extends readonly (infer T)[]
    ? T | undefined
    : undefined
  : K extends keyof A
    ? A[K]
    : undefined;

type GetWithArray<O, K> = K extends []
  ? O
  : K extends [infer Key, ...infer Rest]
    ? O extends Record<PropertyKey, unknown>
      ? GetWithArray<ExtractFromObject<O, Key>, Rest>
      : O extends readonly unknown[]
        ? GetWithArray<ExtractFromArray<O, Key>, Rest>
        : undefined
    : never;

type Path<T> = T extends `${infer Key}.${infer Rest}` ? [Key, ...Path<Rest>] : T extends infer Key ? [Key] : [];

type Get<O, K> = GetWithArray<O, Path<K>>;

export function objectValueByPath<O, K extends string>(obj: O, path: K): Get<O, K>;
export function objectValueByPath(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split(".");

  let currentObj = obj;
  for (const key of keys) {
    const value = currentObj[key];
    if (value === undefined || value === null) {
      return undefined;
    }

    currentObj = value as Record<string, unknown>;
  }

  return currentObj;
}
